import { Divider, IconButton, Menu, StatusIndicator, Tag, Typography, toast } from '@neo4j-ndl/react';
import { EllipsisHorizontalIconOutline, ShieldExclamationIconOutline } from '@neo4j-ndl/react/icons';
import type { FleetManagementTypes } from '@nx/state';
import { fleetManagementApi, useActiveProjectSummary } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { DangerConfirmModal } from '@nx/ui';
import { add, formatDistance } from 'date-fns';
import { useRef, useState } from 'react';

import { ApiErrorBanner } from '../../shared/components';
import { usePermissions } from '../hooks/use-permissions';
import { ConnectDeploymentWizardModal } from '../modals/create-deplyment-modals/connect-deployment-wizard-modal';
import { RegenerateTokenModal } from '../modals/regenerate-token-modal';

export const TokenActions = ({
  token,
  deploymentId,
}: {
  token: FleetManagementTypes.Token | undefined;
  deploymentId: string;
}) => {
  const activeProjectSummary = useActiveProjectSummary();
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isGenTokenModalOpen, setIsGenTokenModalOpen] = useState<boolean>(false);
  const [isRegenTokenModalOpen, setIsRegenTokenModalOpen] = useState<boolean>(false);
  const [isReleaseConfirmationOpen, setIsReleaseConfirmationOpen] = useState<boolean>(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tokenInContext, setTokenInContext] = useState<FleetManagementTypes.Token>();
  const [releaseToken] = fleetManagementApi.useReleaseTokenMutation();
  const [deleteToken] = fleetManagementApi.useDeleteTokenMutation();
  const isTokenUpdateAllowed = usePermissions().isAllowed('tokens', 'write');

  return (
    <div>
      <IconButton
        className="flex w-8 flex-row-reverse"
        onClick={() => {
          setIsOpen(true);
        }}
        ref={anchorEl}
        ariaLabel={'Token actions'}
        isClean={true}
      >
        <EllipsisHorizontalIconOutline />
      </IconButton>
      <Menu isOpen={isOpen} onClose={() => setIsOpen(false)} anchorRef={anchorEl}>
        <Menu.Items>
          {isNullish(token) && (
            <Menu.Item
              title="Create token"
              isDisabled={!isTokenUpdateAllowed}
              onClick={() => setIsGenTokenModalOpen(true)}
            />
          )}
          {isNotNullish(token) && (
            <>
              {token.claimedBy !== 'REVOKED' && (
                <>
                  <Menu.Item
                    title="Regenerate"
                    description="Replace token with a new one"
                    isDisabled={!isTokenUpdateAllowed}
                    onClick={() => {
                      setTokenInContext(token);
                      setIsRegenTokenModalOpen(true);
                    }}
                  />
                  <Divider />
                  <Menu.Item
                    title="Release"
                    className="text-warning-text"
                    isDisabled={!isTokenUpdateAllowed || isNullish(token.claimedBy)}
                    description="Allow a new Neo4j cluster to claim the deployment"
                    onClick={() => {
                      setIsReleaseConfirmationOpen(true);
                    }}
                  />
                </>
              )}
              <Menu.Item
                title="Delete"
                isDisabled={!isTokenUpdateAllowed}
                className="text-danger-text"
                onClick={() => {
                  setIsDeleteConfirmationOpen(true);
                }}
              />
            </>
          )}
        </Menu.Items>
      </Menu>
      <DangerConfirmModal
        title={`Release token?`}
        content={`Reset the deployment to unmonitored. This will allow a new Neo4j cluster to claim this deployment.`}
        confirmButtonText="Release"
        open={isReleaseConfirmationOpen}
        error={null}
        loading={isLoading}
        onClose={() => setIsReleaseConfirmationOpen(false)}
        onConfirm={() => {
          setIsLoading(true);
          releaseToken({ projectId: activeProjectSummary?.id ?? '', deploymentId })
            .then((tokenRes) => {
              if (!tokenRes.error) {
                toast.success(`Token released`, { shouldAutoClose: true });
              } else {
                toast.danger(<ApiErrorBanner error={tokenRes.error} />, { shouldAutoClose: true });
              }
            })
            .catch((err: Error) => {
              toast.danger(err.message, { shouldAutoClose: true });
            })
            .finally(() => {
              setIsLoading(false);
              setIsReleaseConfirmationOpen(false);
            });
        }}
      />
      <DangerConfirmModal
        title={`Delete token?`}
        content={`To connect again, a new token will need to be created.`}
        confirmButtonText="Delete"
        open={isDeleteConfirmationOpen}
        error={null}
        loading={isLoading}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        onConfirm={() => {
          setIsLoading(true);
          deleteToken({ projectId: activeProjectSummary?.id ?? '', deploymentId })
            .then((tokenRes) => {
              if (!tokenRes.error) {
                toast.success(`Token deleted`, { shouldAutoClose: true });
              } else {
                toast.danger(<ApiErrorBanner error={tokenRes.error} />, { shouldAutoClose: true });
              }
            })
            .catch((err: Error) => {
              toast.danger(err.message, { shouldAutoClose: true });
            })
            .finally(() => {
              setIsLoading(false);
              setIsDeleteConfirmationOpen(false);
            });
        }}
      />
      {isGenTokenModalOpen && (
        <ConnectDeploymentWizardModal
          isOpen={isGenTokenModalOpen}
          closeModal={() => setIsGenTokenModalOpen(false)}
          deploymentId={deploymentId}
        />
      )}
      {tokenInContext && (
        <RegenerateTokenModal
          isOpen={isRegenTokenModalOpen}
          closeModal={() => setIsRegenTokenModalOpen(false)}
          deploymentId={deploymentId}
        />
      )}
    </div>
  );
};

export const tokenExpired = (token: FleetManagementTypes.Token | undefined) => {
  if (isNullish(token)) {
    return false;
  }
  const expiryTime = new Date(token.expiryTime).getTime();
  return expiryTime < new Date().getTime();
};

export const tokenExpirySoon = (token: FleetManagementTypes.Token | undefined) => {
  if (isNullish(token) || tokenExpired(token)) {
    return false;
  }
  const expiryTime = new Date(token.expiryTime).getTime();
  return (
    (!token.autoRotated || expiryTime < new Date().getTime()) && expiryTime < add(new Date(), { days: 7 }).getTime()
  );
};

export const tokenExpiryText = (token: FleetManagementTypes.Token | undefined) => {
  if (isNullish(token)) {
    return '';
  }
  const expiryTime = new Date(token.expiryTime).getTime();
  return `${expiryTime < new Date().getTime() ? 'Token expired ' : `Token expires `} ${formatDistance(token.expiryTime, new Date(), { addSuffix: true })}`;
};

export const TokenStatus = ({
  token,
  handleManageToken,
}: {
  token: FleetManagementTypes.Token | undefined;
  handleManageToken?: () => void;
}) => {
  if (isNotNullish(token) && (tokenExpirySoon(token) || tokenExpired(token))) {
    return (
      <div
        {...(handleManageToken && {
          onClick: () => handleManageToken(),
          className: 'cursor-pointer',
        })}
      >
        <Tag type="destructive" size="medium" className="flex w-fit items-center gap-1">
          <ShieldExclamationIconOutline className="h-4 w-4" />
          <Typography variant="body-medium">{tokenExpiryText(token)}</Typography>
        </Tag>
      </div>
    );
  }

  return null;
};

export const TokenUsage = ({ token }: { token: FleetManagementTypes.Token | undefined }) => {
  if (isNullish(token)) {
    return (
      <div className="flex items-center gap-1">
        <StatusIndicator type="unknown" />
        <Typography variant="body-medium">No token created</Typography>
      </div>
    );
  }

  if (isNullish(token.claimedBy)) {
    return (
      <div className="flex items-center gap-1">
        <StatusIndicator type="unknown" />
        <Typography variant="body-medium">Not in use</Typography>
      </div>
    );
  } else if (token.claimedBy === 'REVOKED') {
    return (
      <div className="flex items-center gap-1">
        <StatusIndicator type="danger" />
        <Typography variant="body-medium">Revoked</Typography>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <StatusIndicator type="success" />
      <Typography variant="body-medium">In use</Typography>
    </div>
  );
};
